import { useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { useAuth } from '../contexts/AuthContext'
import { useClubMembership } from '../hooks/useClubMembership'
import { usePageTitle } from '../hooks/usePageTitle'
import { api } from '../lib/api'
import {
  CardPanel,
  ClubButton,
  ClubLinkButton,
  ClubMark,
  ClubPage,
  Container,
  DuesNotice,
  Eyebrow,
  Field,
  MemberCard,
  SignInPrompt,
} from '../components/golf'
// By path, not the barrel: it imports @stripe/react-stripe-js, and the barrel
// is pulled in by every club screen and by clubPages.test.tsx.
import { CardOnFileForm } from '../components/golf/CardOnFileForm'
import { clubAccountReturnUrl } from '../lib/stripe'
import { cn } from '../lib/cn'
import type { ClubMark as Mark, ClubProfileVisibility, User } from '../lib/types'

import { MEMBERSHIP_BILLING_ENABLED } from '../lib/membership'

/** Mirrors Fireside::User::CLUB_MARKS — the club draws each one itself. */
const CLUB_MARKS: Mark[] = ['initial', 'crest', 'pin', 'ball', 'tee', 'bell']

/**
 * Mirrors Fireside::User::CLUB_PROFILE_VISIBILITIES, in the same order.
 *
 * The consequence is written out for each, in the words the club would use to
 * a person, because a privacy control that describes itself in categories
 * ("members only") is one nobody can act on: the question a member is actually
 * asking is *who ends up reading my rounds*. Keep these sentences and
 * docs/fireside/member-profiles.md saying the same thing — that document is
 * what the iOS app is built from, and a control that promises something the
 * API does not do is worse than no control.
 */
const VISIBILITIES: { value: ClubProfileVisibility; title: string; consequence: string }[] = [
  {
    value: 'private',
    title: 'Only me',
    consequence:
      'Nobody else can open your profile — to another member it does not appear to exist. Your name stays on the register and on rounds other members posted; what goes away is the page that gathers them.',
  },
  {
    value: 'members',
    title: 'Members of the club',
    consequence:
      'Members in good standing may read your rounds, your cards and the company you kept — the same people who already see you on the register. Signed-out visitors see nothing at all.',
  },
  {
    value: 'public',
    title: 'Anyone, including the open web',
    consequence:
      'Anybody with the link may read your rounds and cards, signed in or not, and search engines may index the page. Your member number stays a members-only detail, and the members you played with are named only if they chose this setting too.',
  },
]

/**
 * Your account (route map §4 `/clubhouse/account`).
 *
 * Only wired controls are rendered as controls: the display name, the member's
 * mark, Sign out, Manage membership (Stripe's hosted portal), and the dues
 * notice. The email is shown as a stated fact, not a field. Everything the design card shows that has no backend is
 * rendered as a stated fact rather than a dead control —
 *
 *   - Display name and mark: both are REAL controls now — `PATCH /api/v1/me`
 *     takes `display_name` and `club_mark` (Fireside::ProfileUpdates). The
 *     email deliberately stays read-only: it is the key to the door, and
 *     moving it would move the standing and the number with it.
 *   - Preferences: the two correspondence toggles have no model behind them.
 *     Omitted — a switch that silently forgets is worse than no switch. (The
 *     existing `recommendation_consent` / `aggregate_consent` flags are the
 *     Steam recommender's, not the club's; do not repurpose them.)
 *   - Resign your standing: cancelling lives in the Stripe portal, behind
 *     Manage membership. It is deliberately NOT a control of its own here, and
 *     must never be wired to `DELETE /api/v1/me` — that is full GDPR data
 *     deletion, and a control labelled "resign" cannot quietly erase an
 *     account.
 *
 * Keep this list current with docs/fireside/profile-feature-plan.md.
 */

export function MemberAccountScreen() {
  usePageTitle('Your account')
  const { isAuthenticated, user, signOut, refreshSession } = useAuth()
  const { membership, isLoading, failed, refresh: refreshMembership } = useClubMembership()
  const navigate = useNavigate()
  const [openingPortal, setOpeningPortal] = useState(false)
  // null = untouched, so the field DERIVES from the session instead of being
  // seeded by an effect. The session resolves after first paint, and seeding
  // it in an effect both cascades a render and races the member's typing.
  const [draftName, setDraftName] = useState<string | null>(null)
  const [nameError, setNameError] = useState('')
  const [nameSaved, setNameSaved] = useState(false)
  const [savingName, setSavingName] = useState(false)
  const [markError, setMarkError] = useState('')
  const [savingMark, setSavingMark] = useState(false)
  const [visibilityError, setVisibilityError] = useState('')
  const [savingVisibility, setSavingVisibility] = useState(false)
  const [cardPanelOpen, setCardPanelOpen] = useState(false)

  const nameValue = draftName ?? user?.display_name ?? ''

  const createSetupIntent = () => api.post<{ client_secret: string }>('/club/setup_intent')

  // The card is only ON FILE once the server has re-read the subscription —
  // the browser confirming is not the same thing as Stripe having it.
  const lodgeCard = async (setupIntentId: string) => {
    await api.post('/club/payment_method', { setup_intent_id: setupIntentId })
    await refreshMembership()
    setCardPanelOpen(false)
  }

  // The server is the one that validates; whatever it says is already the
  // sentence a member should read, so it goes straight into the field.
  const saveName = async () => {
    setSavingName(true)
    setNameError('')
    try {
      await api.patch<{ user: User }>('/me', { display_name: nameValue })
      // Re-read /me rather than trusting the response body: the nav avatar and
      // the membership card read the same session, and they must not disagree.
      await refreshSession()
      // Back to deriving, so the field reflects whatever the server settled on
      // (it normalises whitespace, and turns "" into no name at all).
      setDraftName(null)
      setNameSaved(true)
    } catch (caught) {
      setNameError(caught instanceof Error ? caught.message : 'We couldn’t enter that name')
    } finally {
      setSavingName(false)
    }
  }

  // No separate save: the mark changes in place, on the avatar and the card.
  const chooseMark = async (mark: Mark) => {
    setSavingMark(true)
    setMarkError('')
    try {
      await api.patch<{ user: User }>('/me', { club_mark: mark })
      await refreshSession()
    } catch (caught) {
      setMarkError(caught instanceof Error ? caught.message : 'We couldn’t change your mark')
    } finally {
      setSavingMark(false)
    }
  }

  // NOT NULL on the server, so a missing value here is an older session
  // payload rather than an unanswered question — fall back to the same default
  // the column carries rather than inventing a fourth state.
  const visibility: ClubProfileVisibility = user?.club_profile_visibility ?? 'members'

  // No separate save, exactly like the mark: the choice IS the control, and a
  // privacy setting that needs a second confirming press is one a member can
  // believe they made without having made it.
  const chooseVisibility = async (choice: ClubProfileVisibility) => {
    setSavingVisibility(true)
    setVisibilityError('')
    try {
      await api.patch<{ user: User }>('/me', { club_profile_visibility: choice })
      await refreshSession()
    } catch (caught) {
      setVisibilityError(
        caught instanceof Error ? caught.message : 'We couldn’t change who may read your book',
      )
    } finally {
      setSavingVisibility(false)
    }
  }

  const name = user?.display_name?.trim() || user?.email?.split('@')[0]

  // `membership: null` with `failed` is "could not ask", never "holds nothing"
  // (ClubMembershipContext). The clubhouse gate fails OPEN on it and lets the
  // member in, and this page must not then sell them a standing they may well
  // hold: the standing region says it could not be read and offers to ask
  // again — no checkout, no terms, no "No paid standing yet".
  const standingUnreadable = failed && !isLoading

  const handleSignOut = async () => {
    await signOut()
    navigate({ to: '/' })
  }

  // Stripe hosts subscription management; the club builds no UI for it. Full
  // page assignment because the destination is Stripe's host.
  const openBillingPortal = async () => {
    setOpeningPortal(true)
    try {
      const { portal_url } = await api.post<{ portal_url: string }>('/club/portal_session')
      window.location.assign(portal_url)
    } catch {
      setOpeningPortal(false)
    }
  }

  if (!isAuthenticated) {
    return (
      <ClubPage chrome="clubhouse">
        <Container className="py-16">
          <Eyebrow>The Clubhouse</Eyebrow>
          <h1 className="mb-3.5 mt-4 font-display text-[clamp(2.25rem,4.4vw,3.25rem)] font-medium leading-[1.04] tracking-[-0.014em] text-pine">
            Your account
          </h1>
          <p className="mb-9 max-w-[52ch] text-[15.5px] text-ink-soft">
            Your standing, your particulars, and how the club writes to you.
          </p>
          <SignInPrompt what="your account" />
        </Container>
      </ClubPage>
    )
  }

  return (
    <ClubPage chrome="clubhouse">
      <Container className="pt-16">
        {/* "The Clubhouse · Member No. 007" (member-account.html:201) — the
            number the club actually struck, already formatted on the wire,
            and nothing appended while there is none. */}
        <Eyebrow>
          The Clubhouse
          {membership?.member_number ? ` · Member ${membership.member_number}` : ''}
        </Eyebrow>
        <h1 className="mb-3.5 mt-4 font-display text-[clamp(2.25rem,4.4vw,3.25rem)] font-medium leading-[1.04] tracking-[-0.014em] text-pine">
          Your account
        </h1>
        <p className="max-w-[52ch] text-[15.5px] text-ink-soft">
          Your standing, your particulars, and how the club writes to you.
        </p>

        {/* ── I. Membership ── */}
        <section className="mt-15">
          <div className="mb-6.5 flex items-baseline gap-4.5">
            <span className="font-display text-sm text-brass-deep">I.</span>
            <h2 className="font-display text-2xl font-medium text-pine">Membership</h2>
            <div aria-hidden="true" className="club-rule flex-1" />
          </div>

          <div className="grid items-start gap-13 lg:grid-cols-[380px_1fr]">
            <MemberCard
              name={name ?? 'Member'}
              tier={membership?.tier_name ?? 'Member'}
              number={membership?.member_number ?? undefined}
              founding={membership?.tier === 'founding'}
              since={membership?.standing_since}
              mark={user?.club_mark}
            />

            {standingUnreadable ? (
              <div>
                <p className="text-[15px] text-ink">We couldn’t read your standing just now.</p>
                <ClubButton
                  variant="ghost"
                  className="mt-6"
                  onClick={() => void refreshMembership()}
                >
                  Try again
                </ClubButton>
              </div>
            ) : (
              <div>
                <dl className="border-t border-brass">
                  {[
                    {
                      k: 'Standing',
                      v: membership
                        ? `${membership.tier_name ?? membership.tier}${membership.active ? '' : ` — ${membership.status}`}`
                        : 'No paid standing yet',
                    },
                    { k: 'Member no.', v: membership?.member_number ?? 'Not yet struck' },
                    // Was a hardcoded 'MMXXVI' — the only fabricated value on
                    // this page. Now the date the club actually recorded, and
                    // absent entirely when there is no standing to date.
                    ...(membership?.standing_since
                      ? [{
                          k: 'Standing since',
                          v: new Date(membership.standing_since).toLocaleDateString(),
                        }]
                      : []),
                    {
                      k: membership?.active ? 'Renews' : 'Dues',
                      v: membership?.current_period_end
                        ? new Date(membership.current_period_end).toLocaleDateString()
                        : 'None settled',
                    },
                  ].map((row) => (
                    <div
                      key={row.k}
                      className="flex items-baseline justify-between gap-6 border-b border-brass/25 px-0.5 py-3"
                    >
                      <dt className="text-[11px] font-semibold uppercase tracking-[0.16em] text-brass-deep">
                        {row.k}
                      </dt>
                      <dd className="text-right text-sm text-ink">{row.v}</dd>
                    </div>
                  ))}
                </dl>

                <div className="mt-6 flex flex-wrap items-center gap-5">
                  {membership ? (
                    <ClubButton variant="ghost" onClick={openBillingPortal} disabled={openingPortal}>
                      {openingPortal ? 'Opening…' : 'Manage membership'}
                    </ClubButton>
                  ) : (
                    <ClubLinkButton
                      to={MEMBERSHIP_BILLING_ENABLED ? '/join/checkout' : '/membership'}
                      variant="ghost"
                    >
                      {MEMBERSHIP_BILLING_ENABLED ? 'Take up a standing' : 'Read the terms'}
                    </ClubLinkButton>
                  )}
                  <ClubLinkButton to="/the-club" variant="ghost" size="sm">
                    View the charter →
                  </ClubLinkButton>
                </div>

                <p className="mt-3.5 text-xs text-ink-soft">
                  {MEMBERSHIP_BILLING_ENABLED
                    ? 'Dues are settled securely via Stripe. The steward never sees your card.'
                    : 'The front desk is not yet taking dues, so no standing has been struck. The clubhouse is open to you regardless.'}
                </p>
              </div>
            )}
          </div>

          {/*
            The renewal cliff. Both the "no card yet" and the "already failed"
            cases route to the same place — Stripe's portal is where a card is
            added either way — so this is one notice, not two.
          */}
          {membership?.needs_payment_method && !cardPanelOpen && (
            <DuesNotice
              inArrears={membership.in_arrears}
              onAddCard={() => setCardPanelOpen(true)}
            />
          )}

          {/* One `flag` CTA per page, by construction: the notice's only job is
              to route here, so when the panel is open the notice is gone and
              "Lodge the card" is the page's single primary. */}
          <CardPanel
            open={cardPanelOpen}
            onClose={() => setCardPanelOpen(false)}
            onCreateIntent={createSetupIntent}
          >
            {({ clientSecret, onBack }) => (
              <CardOnFileForm
                clientSecret={clientSecret}
                returnUrl={clubAccountReturnUrl()}
                onConfirmed={lodgeCard}
                onCancel={onBack}
              />
            )}
          </CardPanel>
        </section>

        {/* ── II. Particulars ── */}
        <section className="mt-15">
          <div className="mb-6.5 flex items-baseline gap-4.5">
            <span className="font-display text-sm text-brass-deep">II.</span>
            <h2 className="font-display text-2xl font-medium text-pine">Particulars</h2>
            <div aria-hidden="true" className="club-rule flex-1" />
          </div>

          <dl className="max-w-[560px] border-t border-brass">
            <div className="border-b border-brass/25 px-0.5 py-3.5">
              <dt className="text-[11px] font-semibold uppercase tracking-[0.16em] text-brass-deep">
                Email
              </dt>
              <dd className="mt-1.5 text-[15px] text-ink">{user?.email ?? '—'}</dd>
              <dd className="mt-1.5 text-xs text-ink-soft">
                Your sign-in address, and the club&rsquo;s only way to reach you. It cannot be
                changed: it <em>is</em> your key to the door, and moving it would mean moving your
                standing and your number with it.
              </dd>
            </div>
          </dl>

          <div className="mt-8 max-w-[560px]">
            <Field
              label="Display name"
              value={nameValue}
              error={nameError}
              help="How the register reads your name. Leave it empty and it reads your address instead."
              maxLength={40}
              onChange={(event) => {
                setDraftName(event.target.value)
                setNameError('')
                setNameSaved(false)
              }}
              disabled={savingName}
            />
            <div className="flex flex-wrap items-center gap-4">
              <ClubButton
                variant="ghost"
                size="sm"
                onClick={saveName}
                disabled={savingName || nameValue === (user?.display_name ?? '')}
              >
                {savingName ? 'Entering…' : 'Enter it in the register'}
              </ClubButton>
              {nameSaved && (
                <p role="status" className="text-[13px] text-fairway">
                  Entered.
                </p>
              )}
            </div>

            <fieldset className="mt-8 border-t border-brass/25 pt-5">
              <legend className="text-[11px] font-semibold uppercase tracking-[0.16em] text-brass-deep">
                Your mark
              </legend>
              <p className="mb-3.5 mt-1.5 text-xs text-ink-soft">
                Worn beside your name, and struck on your card.
              </p>
              {/* radiogroup, not switches: clubPages pins that this page renders
                  no control it cannot honour, and a switch there means an
                  unbacked preference toggle. These are backed. */}
              <div role="radiogroup" aria-label="Your mark" className="flex flex-wrap gap-3">
                {CLUB_MARKS.map((option) => (
                  <button
                    key={option}
                    type="button"
                    role="radio"
                    aria-checked={(user?.club_mark ?? 'initial') === option}
                    aria-label={option}
                    onClick={() => chooseMark(option)}
                    disabled={savingMark}
                    className={cn(
                      'grid size-12 place-items-center rounded-full border bg-parchment transition',
                      (user?.club_mark ?? 'initial') === option
                        ? 'border-brass-deep ring-2 ring-brass-deep/35'
                        : 'border-brass/45 hover:border-brass',
                    )}
                  >
                    {option === 'initial' ? (
                      <span className="font-display text-[17px] font-semibold text-fairway">
                        {name?.trim()?.charAt(0)?.toUpperCase() ?? '·'}
                      </span>
                    ) : (
                      <ClubMark mark={option} className="size-6" />
                    )}
                  </button>
                ))}
              </div>
              {markError && (
                <p role="alert" className="mt-3 text-[13px] text-flag">
                  {markError}
                </p>
              )}
            </fieldset>

            {/* Who may read your book — the profile's own audience.
                A radiogroup rather than switches, for the same reason the mark
                is one: these are backed by a real column and a real gate. */}
            <fieldset className="mt-8 border-t border-brass/25 pt-5">
              <legend className="text-[11px] font-semibold uppercase tracking-[0.16em] text-brass-deep">
                Who may read your book
              </legend>
              <p className="mb-4 mt-1.5 text-xs text-ink-soft">
                Your profile gathers the rounds you have posted, the cards behind them and who
                you played with. Nobody sees your email address at any setting, and no reader
                is ever told which setting you keep.
              </p>

              <div role="radiogroup" aria-label="Who may read your book" className="border-t border-brass">
                {VISIBILITIES.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    role="radio"
                    aria-checked={visibility === option.value}
                    onClick={() => chooseVisibility(option.value)}
                    disabled={savingVisibility}
                    className="grid w-full grid-cols-[22px_1fr] items-start gap-4 border-b border-brass/25 px-0.5 py-4 text-left"
                  >
                    <span
                      aria-hidden="true"
                      className={cn(
                        'mt-1 size-4.5 rounded-full bg-parchment',
                        visibility === option.value
                          ? 'border-[5px] border-fairway'
                          : 'border border-brass',
                      )}
                    />
                    <span>
                      <span className="block font-display text-[16px] text-pine">
                        {option.title}
                      </span>
                      <span className="mt-1 block text-[13px] text-ink-soft">
                        {option.consequence}
                      </span>
                    </span>
                  </button>
                ))}
              </div>

              {visibilityError && (
                <p role="alert" className="mt-3 text-[13px] text-flag">
                  {visibilityError}
                </p>
              )}

              <p className="mt-4 text-xs text-ink-soft">
                <Link
                  className="text-fairway underline"
                  to="/members/$userId"
                  params={{ userId: user?.id ?? '' }}
                >
                  Read your own book
                </Link>{' '}
                — the same page, exactly as you have let others see it.
              </p>
            </fieldset>
          </div>
        </section>

        {/* ── III. The Exit ── */}
        <section className="mt-15">
          <div className="mb-6.5 flex items-baseline gap-4.5">
            <span className="font-display text-sm text-brass-deep">III.</span>
            <h2 className="font-display text-2xl font-medium text-pine">The Exit</h2>
            <div aria-hidden="true" className="club-rule flex-1" />
          </div>

          <div className="flex flex-col justify-between gap-7 sm:flex-row sm:items-center">
            <p className="max-w-[52ch] font-display text-[13.5px] italic text-ink-soft">
              Leaving is never a formality here. Sign out whenever you must — your name stays in the
              charter, and the door is where you left it.
            </p>
            <ClubButton variant="ghost" onClick={handleSignOut}>
              Sign out
            </ClubButton>
          </div>
        </section>
      </Container>
    </ClubPage>
  )
}
